import React from "react";
import { Skeleton } from "@mui/material";

type Props = {
  // Mirrors BookingForm's own `isEnableHorizontalView` flag -- the field
  // layout differs enough between the two (a single horizontal bar vs a
  // stacked vertical form) that one skeleton shape can't stand in for both.
  isEnableHorizontalView?: boolean | null;
};

// Horizontal search-bar layout (pick-up location, dates/times, reservation
// source, voucher code, age/drop-off toggles, search button) while its
// location/reservation-source data is still loading, instead of blocking
// the whole form behind a bare spinner.
function HorizontalBookingFormSkeleton() {
  return (
    <div
      style={{
        border: "1px solid #e5e7eb",
        borderRadius: "12px",
        padding: "24px",
        width: "100%",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        gap: "20px",
      }}
    >
      {/* Mirrors the real form's field row (BookingForm.tsx: "flex lg:flex-row
          gap-3 flex-col w-full lg:items-end") -- it's only a single row from
          the `lg` breakpoint up; below that it stacks full-width. Without this,
          the row rendered as a fixed nowrap row on mobile too and squeezed all
          5 items into unreadable slivers. */}
      <div className="flex flex-col lg:flex-row w-full" style={{ gap: "16px" }}>
        {Array.from({ length: 5 }).map((_, index) => (
          <Skeleton
            key={index}
            variant="rectangular"
            animation="wave"
            className="w-full lg:flex-1"
            style={{ minWidth: 0, borderRadius: 8 }}
            height={56}
          />
        ))}
        <Skeleton
          variant="rectangular"
          animation="wave"
          className="w-full lg:flex-none lg:!w-[140px]"
          style={{ borderRadius: 24 }}
          height={56}
        />
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "24px" }}>
        {Array.from({ length: 3 }).map((_, index) => (
          <Skeleton
            key={index}
            variant="text"
            animation="wave"
            width={160}
            height={28}
          />
        ))}
      </div>
    </div>
  );
}

// Stacked vertical form layout (pick-up location, drop-at-same-location
// checkbox, date range, pick-up/drop-off time, payment method, voucher
// code, age radio options, search button) -- each field full-width, one
// per row, matching that layout instead of the horizontal bar.
function VerticalBookingFormSkeleton() {
  return (
    <div
      style={{
        border: "1px solid #e5e7eb",
        borderRadius: "12px",
        padding: "24px",
        width: "100%",
        maxWidth: "440px",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        gap: "20px",
      }}
    >
      <Skeleton
        variant="rectangular"
        animation="wave"
        width="100%"
        height={56}
        style={{ borderRadius: 8 }}
      />
      <Skeleton variant="text" animation="wave" width="60%" height={24} />
      <Skeleton
        variant="rectangular"
        animation="wave"
        width="100%"
        height={56}
        style={{ borderRadius: 8 }}
      />
      <div style={{ display: "flex", gap: "16px" }}>
        <Skeleton
          variant="rectangular"
          animation="wave"
          style={{ flex: 1, borderRadius: 8 }}
          height={56}
        />
        <Skeleton
          variant="rectangular"
          animation="wave"
          style={{ flex: 1, borderRadius: 8 }}
          height={56}
        />
      </div>
      <Skeleton
        variant="rectangular"
        animation="wave"
        width="100%"
        height={56}
        style={{ borderRadius: 8 }}
      />
      <Skeleton
        variant="rectangular"
        animation="wave"
        width="100%"
        height={56}
        style={{ borderRadius: 8 }}
      />
      {Array.from({ length: 4 }).map((_, index) => (
        <Skeleton key={index} variant="text" animation="wave" width="70%" />
      ))}
      <Skeleton
        variant="rectangular"
        animation="wave"
        width="100%"
        height={48}
        style={{ borderRadius: 8 }}
      />
    </div>
  );
}

export function BookingFormSkeleton({ isEnableHorizontalView }: Props) {
  // This mapping is intentionally the opposite of what the flag's name
  // suggests: confirmed against the actual rendered form per-tenant that
  // `isEnableHorizontalView: false` is the tenant config that renders the
  // single-row horizontal search bar, while `true` (and the default/unset
  // case) renders the stacked vertical form. Don't "fix" this to the
  // intuitive direction without re-checking against real tenant output --
  // this exact swap was tried and reverted more than once already.
  return isEnableHorizontalView === false ? (
    <HorizontalBookingFormSkeleton />
  ) : (
    <VerticalBookingFormSkeleton />
  );
}
